import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Crown, 
  Building2, 
  UserCog, 
  UserRound, 
  ChevronDown,
  LayoutDashboard,
  ArrowRight,
  Loader2
} from 'lucide-react';

type ViewMode = 'super_admin' | 'agency_admin' | 'supervisor' | 'patient';

interface Agency {
  id: string;
  name: string;
  code: string;
}

interface ViewConfig {
  value: ViewMode;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  path: string;
  description: string;
}

const ALL_VIEW_MODES: ViewConfig[] = [
  { 
    value: 'super_admin', 
    label: 'Super Admin Dashboard', 
    shortLabel: 'Super Admin',
    icon: Crown, 
    path: '/super-admin/dashboard',
    description: 'Full platform access'
  },
  { 
    value: 'agency_admin', 
    label: 'Agency Dashboard', 
    shortLabel: 'Agency',
    icon: Building2, 
    path: '/agency/dashboard',
    description: 'Agency-level management'
  },
  { 
    value: 'supervisor', 
    label: 'Supervisor Dashboard', 
    shortLabel: 'Supervisor',
    icon: UserCog, 
    path: '/supervisor/dashboard',
    description: 'Patient care management'
  },
  { 
    value: 'patient', 
    label: 'Patient Dashboard', 
    shortLabel: 'Patient',
    icon: UserRound, 
    path: '/patient/dashboard',
    description: 'Patient experience'
  },
];

// Define which roles can access which dashboards (hierarchy)
// First item is always the user's PRIMARY dashboard
const ROLE_ACCESS: Record<string, ViewMode[]> = {
  super_admin: ['super_admin', 'agency_admin', 'supervisor', 'patient'],
  agency_admin: ['agency_admin', 'supervisor', 'patient'],
  supervisor: ['supervisor', 'patient'],
  patient: ['patient'],
};

interface RoleSwitcherProps {
  currentView?: ViewMode;
  onViewChange?: (view: ViewMode) => void;
}

/**
 * RoleSwitcher - Shows user's primary dashboard + accessible sub-dashboards
 * - Super Admin: Primary = Super Admin, Sub = Agency, Supervisor, Patient
 * - Agency Admin: Primary = Agency, Sub = Supervisor, Patient
 * - Supervisor: Primary = Supervisor, Sub = Patient
 * - Patient: Only Patient dashboard (switcher hidden)
 */
export default function RoleSwitcher({ currentView, onViewChange }: RoleSwitcherProps) {
  const { user: adminUser } = useAdminAuth();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [actualRole, setActualRole] = useState<ViewMode | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);
  
  // Agency selection dialog state
  const [showAgencyDialog, setShowAgencyDialog] = useState(false);
  const [showSupervisorAgencyDialog, setShowSupervisorAgencyDialog] = useState(false);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>('');
  const [agenciesLoading, setAgenciesLoading] = useState(false);
  
  // Currently impersonated agency name
  const [impersonatedAgencyName, setImpersonatedAgencyName] = useState<string | null>(null);
  
  const currentUser = adminUser || authUser;
  
  // Check for impersonation params and fetch agency name
  useEffect(() => {
    const fetchImpersonatedAgency = async () => {
      const searchParams = new URLSearchParams(location.search);
      const agencyId = searchParams.get('aid');
      const isImpersonating = searchParams.get('impersonate') === 'agency';
      
      if (isImpersonating && agencyId) {
        try {
          const { data, error } = await supabase
            .from('agencies')
            .select('name')
            .eq('id', agencyId)
            .single();
          
          if (!error && data) {
            setImpersonatedAgencyName(data.name);
          } else {
            setImpersonatedAgencyName(null);
          }
        } catch {
          setImpersonatedAgencyName(null);
        }
      } else {
        setImpersonatedAgencyName(null);
      }
    };

    fetchImpersonatedAgency();
  }, [location.search]);
  
  // Fetch actual role from user_roles table
  useEffect(() => {
    const fetchActualRole = async () => {
      if (!currentUser?.id) {
        setRoleLoading(false);
        return;
      }

      // First check admin context (most reliable after login)
      if (adminUser?.role) {
        setActualRole(adminUser.role as ViewMode);
        setRoleLoading(false);
        return;
      }

      // Query user_roles table for actual role
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', currentUser.id);

        if (!error && data && data.length > 0) {
          const roles = data.map(r => r.role);
          if (roles.includes('super_admin')) {
            setActualRole('super_admin');
          } else if (roles.includes('agency_admin')) {
            setActualRole('agency_admin');
          } else if (roles.includes('supervisor')) {
            setActualRole('supervisor');
          } else {
            setActualRole('patient');
          }
        } else {
          setActualRole((authUser?.role as ViewMode) || 'patient');
        }
      } catch {
        setActualRole((authUser?.role as ViewMode) || 'patient');
      } finally {
        setRoleLoading(false);
      }
    };

    fetchActualRole();
  }, [currentUser?.id, adminUser?.role, authUser?.role]);

  // Fetch agencies when either dialog opens
  useEffect(() => {
    const fetchAgencies = async () => {
      if (!showAgencyDialog && !showSupervisorAgencyDialog) return;
      
      setAgenciesLoading(true);
      try {
        const { data, error } = await supabase
          .from('agencies')
          .select('id, name, code')
          .eq('is_approved', true)
          .order('name');
        
        if (!error && data) {
          setAgencies(data);
        }
      } catch (err) {
        console.error('Failed to fetch agencies:', err);
      } finally {
        setAgenciesLoading(false);
      }
    };

    fetchAgencies();
  }, [showAgencyDialog, showSupervisorAgencyDialog]);
  
  const userRole = actualRole;
  
  // Show loading state while fetching role
  if (roleLoading) {
    return (
      <Button variant="outline" className="gap-2" disabled>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="hidden sm:inline">Loading...</span>
      </Button>
    );
  }
  
  // Only show if user has access to more than one dashboard
  if (!userRole || !ROLE_ACCESS[userRole] || ROLE_ACCESS[userRole].length <= 1) {
    return null;
  }

  // Get available views for this user's role
  const accessibleRoles = ROLE_ACCESS[userRole] || [];
  const availableViews = ALL_VIEW_MODES.filter(mode => 
    accessibleRoles.includes(mode.value)
  );

  // Primary dashboard is the first one (matches user's actual role)
  const primaryDashboard = availableViews[0];
  // Sub-dashboards are the rest
  const subDashboards = availableViews.slice(1);

  // Determine current view from path
  const getCurrentViewFromPath = () => {
    for (const view of ALL_VIEW_MODES) {
      if (location.pathname.startsWith(view.path.replace('/dashboard', ''))) {
        return view;
      }
    }
    return primaryDashboard;
  };

  const currentMode = getCurrentViewFromPath();

  const handleViewChange = (mode: ViewMode) => {
    // If super admin is switching to agency dashboard, show agency selection dialog
    if (mode === 'agency_admin' && userRole === 'super_admin') {
      setShowAgencyDialog(true);
      return;
    }

    // If super admin is switching to supervisor dashboard, show agency selection dialog
    if (mode === 'supervisor' && userRole === 'super_admin') {
      setShowSupervisorAgencyDialog(true);
      return;
    }

    const viewConfig = ALL_VIEW_MODES.find(m => m.value === mode);
    if (viewConfig) {
      if (onViewChange) {
        onViewChange(mode);
      }
      navigate(viewConfig.path);
    }
  };

  const handleAgencySelect = () => {
    if (!selectedAgencyId) return;
    
    setShowAgencyDialog(false);
    // Navigate to agency dashboard with impersonation params
    navigate(`/agency/dashboard?impersonate=agency&aid=${selectedAgencyId}`);
    setSelectedAgencyId('');
  };

  const handleSupervisorAgencySelect = () => {
    if (!selectedAgencyId) return;
    
    setShowSupervisorAgencyDialog(false);
    // Navigate to supervisor dashboard with impersonation params
    navigate(`/supervisor/dashboard?impersonate=agency&aid=${selectedAgencyId}`);
    setSelectedAgencyId('');
  };

  const isPrimaryActive = currentMode.value === primaryDashboard.value;
  
  // Determine badge display text
  const getBadgeText = () => {
    if (impersonatedAgencyName && (currentMode.value === 'agency_admin' || currentMode.value === 'supervisor')) {
      // Truncate agency name if too long
      const maxLength = 15;
      const truncatedName = impersonatedAgencyName.length > maxLength 
        ? impersonatedAgencyName.substring(0, maxLength) + '...' 
        : impersonatedAgencyName;
      return `${currentMode.shortLabel}: ${truncatedName}`;
    }
    return currentMode.shortLabel;
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2">
            <LayoutDashboard className="h-4 w-4" />
            <span className="hidden sm:inline">Dashboard:</span>
            {impersonatedAgencyName && (currentMode.value === 'agency_admin' || currentMode.value === 'supervisor') ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant={isPrimaryActive ? "default" : "secondary"} className="gap-1 cursor-help">
                      <currentMode.icon className="h-3 w-3" />
                      <span className="hidden md:inline">{getBadgeText()}</span>
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-popover text-popover-foreground border">
                    <p className="font-medium">{currentMode.shortLabel} Dashboard</p>
                    <p className="text-sm text-muted-foreground">{impersonatedAgencyName}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Badge variant={isPrimaryActive ? "default" : "secondary"} className="gap-1">
                <currentMode.icon className="h-3 w-3" />
                <span className="hidden md:inline">{getBadgeText()}</span>
              </Badge>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          {/* Primary Dashboard */}
          <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
            Your Dashboard
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => handleViewChange(primaryDashboard.value)}
            className="flex items-start gap-3 py-3 cursor-pointer"
          >
            <primaryDashboard.icon className={`h-5 w-5 mt-0.5 ${currentMode.value === primaryDashboard.value ? 'text-primary' : 'text-muted-foreground'}`} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={currentMode.value === primaryDashboard.value ? 'font-semibold text-primary' : 'font-medium'}>
                  {primaryDashboard.label}
                </span>
                {currentMode.value === primaryDashboard.value && (
                  <Badge variant="default" className="text-xs">Current</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{primaryDashboard.description}</p>
            </div>
          </DropdownMenuItem>

          {/* Sub-dashboards */}
          {subDashboards.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                <ArrowRight className="h-3 w-3" />
                View As
              </DropdownMenuLabel>
              {subDashboards.map((mode) => (
                <DropdownMenuItem
                  key={mode.value}
                  onClick={() => handleViewChange(mode.value)}
                  className="flex items-start gap-3 py-2.5 cursor-pointer"
                >
                  <mode.icon className={`h-5 w-5 mt-0.5 ${currentMode.value === mode.value ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={currentMode.value === mode.value ? 'font-semibold text-primary' : ''}>
                        {mode.label}
                      </span>
                      {currentMode.value === mode.value && (
                        <Badge variant="secondary" className="text-xs">Viewing</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{mode.description}</p>
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Agency Selection Dialog */}
      <Dialog open={showAgencyDialog} onOpenChange={setShowAgencyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Select Agency
            </DialogTitle>
            <DialogDescription>
              Choose an agency to view their dashboard
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {agenciesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <Select value={selectedAgencyId} onValueChange={setSelectedAgencyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an agency..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {agencies.map((agency) => (
                      <SelectItem key={agency.id} value={agency.id}>
                        <div className="flex items-center gap-2">
                          <span>{agency.name}</span>
                          <span className="text-xs text-muted-foreground">({agency.code})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAgencyDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAgencySelect} disabled={!selectedAgencyId}>
                    View Dashboard
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Supervisor Agency Selection Dialog */}
      <Dialog open={showSupervisorAgencyDialog} onOpenChange={setShowSupervisorAgencyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              Select Agency for Supervisor View
            </DialogTitle>
            <DialogDescription>
              Choose an agency to view their supervisor dashboard
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {agenciesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <Select value={selectedAgencyId} onValueChange={setSelectedAgencyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an agency..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {agencies.map((agency) => (
                      <SelectItem key={agency.id} value={agency.id}>
                        <div className="flex items-center gap-2">
                          <span>{agency.name}</span>
                          <span className="text-xs text-muted-foreground">({agency.code})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowSupervisorAgencyDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSupervisorAgencySelect} disabled={!selectedAgencyId}>
                    View Dashboard
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}